'use client'

/**
 * Hook: fetch workspace credit balance + lifetime purchased for the active workspace.
 *
 * Lifts the logic that previously lived inline in AppSidebarWorkspaceMenu so the
 * mobile footer / workspace drawer can render the same credit ring without
 * duplicating the Supabase query.
 *
 * Returns:
 *   - remaining: balance (clamped ≥ 0). 0 while loading or when no row exists.
 *   - lifetimePurchased: total purchased over the workspace's lifetime.
 *   - usagePercent: remaining / lifetimePurchased * 100, or null when purchased
 *     is 0 (no plan / pre-purchase). Use null to render the ring as track-only.
 *   - loading: true until the first fetch resolves.
 *
 * Re-runs whenever `workspaceId` changes. Cleans up on unmount via a cancelled flag.
 *
 * NOTE: this is a one-shot fetch, not realtime. For realtime updates we'd need
 * to subscribe to `workspace_credits` changes — out of scope for the mobile work.
 */

import { useEffect, useState } from 'react'
import { createClient } from '@/utils/supabase/client'

export type WorkspaceCredits = {
  remaining: number
  lifetimePurchased: number
  usagePercent: number | null
  loading: boolean
}

export function useWorkspaceCredits(workspaceId: string | null | undefined): WorkspaceCredits {
  const [remaining, setRemaining] = useState<number>(0)
  const [lifetimePurchased, setLifetimePurchased] = useState<number>(0)
  const [usagePercent, setUsagePercent] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(!!workspaceId)

  useEffect(() => {
    if (!workspaceId) {
      setRemaining(0)
      setLifetimePurchased(0)
      setUsagePercent(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    const supabase = createClient()

    void (async () => {
      const { data: credits } = await supabase
        .from('workspace_credits' as any)
        .select('balance, lifetime_purchased')
        .eq('workspace_id', workspaceId)
        .maybeSingle()

      if (cancelled) return

      const balance = Math.max(0, Number((credits as any)?.balance ?? 0))
      const purchased = Number((credits as any)?.lifetime_purchased ?? 0)

      setRemaining(balance)
      setLifetimePurchased(purchased)
      setUsagePercent(
        purchased <= 0
          ? null
          : Math.max(0, Math.min(100, (balance / purchased) * 100)),
      )
      setLoading(false)
    })()

    return () => {
      cancelled = true
    }
  }, [workspaceId])

  return { remaining, lifetimePurchased, usagePercent, loading }
}
